import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Card,
  Grid,
  TextInput,
  Select,
  Box,
  Text,
  Stepper,
  Divider,
  Space,
  Flex,
  UnstyledButton,
  Badge,
  Loader,
  Modal,
  Stack,
} from "@mantine/core";
import { IconExternalLink } from "@tabler/icons-react";
import axios from "axios";
import { Link } from "react-router-dom";

import { UserGroupList, UserGroupForm } from "./";
import entityColor from "@util/entityColor";
import entityIcon from "@util/entityIcon";
import {
  tableCategoryConfig,
  RecipeCard,
} from "@components/ReportRecipe/ReportRecipeList";
import { SnapshotCard } from "@components/ReportRecipe/ReportRecipeRunner/ReportSnapshotList";
import { EffortContainer } from "@components/shared/UserAdd/UserGroup";
import { ActionBar } from "@components/shared";
import { SnapshotQueryVisualizer } from "@components/ReportRecipe";

export default function UserGroupManagement({ locationId, orgId }) {
  const [isCreating, setIsCreating] = useState(false);

  return (
    <div>
      <ActionBar containerProps={{ mb: "md" }} color={entityColor.user}>
        <Button
          onClick={() => setIsCreating(!isCreating)}
          color={entityColor.user}
          variant={isCreating ? "light" : "filled"}
        >
          {isCreating ? "View Segments" : "Create Segment"}
        </Button>
      </ActionBar>
      {isCreating ? (
        <Card>
          <CreateUserGroup
            onSuccess={() => setIsCreating(false)}
            locationId={locationId}
            orgId={orgId}
          />
        </Card>
      ) : (
        <UserGroupList locationId={locationId} orgId={orgId} />
      )}
    </div>
  );
}

const sourceModes = [
  {
    label: "Source users from a snapshot",
    value: "snapshot",
    color: entityColor.report,
    icon: entityIcon.snapshot(18),
  },
  {
    label: "Source users from an effort",
    value: "effort",
    color: entityColor.effort,
    icon: entityIcon.effort(18),
  },
];

const CreateUserGroup = ({ onSuccess, locationId, orgId }) => {
  const [reportRecipe, setReportRecipe] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [sourceMode, setSourceMode] = useState(sourceModes[0].value);
  const [effort, setEffort] = useState(null);

  const showForm = reportRecipe && snapshot;

  function reset() {
    setReportRecipe(null);
    setSnapshot(null);
    setEffort(null);
  }

  const reqData = {
    location_id: locationId,
    organization_id: orgId,
  };

  return (
    <div>
      {sourceMode === "snapshot" && (
        <>
          <Section
            title="Source your users"
            description="Where do you want to source your users from?"
            transparent={reportRecipe ? true : false}
            completed={reportRecipe ? true : false}
            extra={
              <SourceSelect
                onChange={(e) => {
                  setSourceMode(e);
                  reset();
                }}
                value={sourceMode}
              />
            }
          >
            {!reportRecipe && (
              <Text size="md" mb="md" fw={600}>
                Select a report recipe first to see available snapshots
              </Text>
            )}
            <RecipeSelect
              reqData={reqData}
              onChange={(e) => {
                setReportRecipe(e);
                setSnapshot(null);
              }}
              value={reportRecipe?.id}
            />
          </Section>
          {reportRecipe && (
            <>
              <Space h="xl" />
              <Section
                title="Select a snapshot"
                description="Choose a snapshot to source your users from"
                completed={snapshot ? true : false}
                transparent={snapshot ? true : false}
              >
                <SnapshotSelect
                  reqData={{
                    ...reqData,
                    report_recipe_id: reportRecipe.id,
                  }}
                  onChange={(e) => {
                    setSnapshot(e);
                  }}
                  value={snapshot?.id}
                >
                  <Flex gap="xs" direction={{ base: "column", md: "row" }}>
                    <Button
                      color={entityColor.report}
                      size="sm"
                      onClick={() => setReportRecipe(null)}
                    >
                      Select a different recipe?
                    </Button>
                    <Button
                      color={entityColor.report}
                      variant="light"
                      size="sm"
                      onClick={() => setReportRecipe(null)}
                      leftSection={<IconExternalLink size={12} />}
                      component={Link}
                      to={`/report-recipes/${reportRecipe.id}`}
                    >
                      View Recipe
                    </Button>
                  </Flex>
                </SnapshotSelect>
              </Section>
            </>
          )}
        </>
      )}
      {sourceMode === "effort" && (
        <>
          <Section
            title="Source your users"
            description="Where do you want to source your users from?"
            transparent={effort ? true : false}
            completed={effort ? true : false}
            extra={
              <SourceSelect
                onChange={(e) => {
                  setSourceMode(e);
                  reset();
                }}
                value={sourceMode}
              />
            }
          >
            <EffortSelect
              reqData={reqData}
              value={effort?.id}
              onChange={(e) => {
                setEffort(e);
              }}
            />
          </Section>
          {effort && (
            <Section
              title="Effort Details"
              description="Here are the users from this effort"
              transparent={true}
            >
              <EffortContainer
                id={effort.id}
                creatingUserGroup={true}
                fetchData={() => true}
                reqData={reqData}
              />
            </Section>
          )}
        </>
      )}
      {showForm && (
        <>
          <Space h="xl" />
          <Section
            title="Create your segment"
            description="Give your segment a title and description"
            completed
          >
            <UserGroupForm
              info={{
                name: reportRecipe.title + " - " + snapshot.title,
                description: "This is a description",
              }}
              onSuccess={() => {
                onSuccess();
              }}
              reqData={{
                ...reqData,
                report_recipe_id: reportRecipe.id,
                report_snapshot_id: snapshot.id,
              }}
            />
          </Section>
          <Space h="xl" />
        </>
      )}
    </div>
  );
};

function RecipeSelect({ onChange, value, reqData }) {
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRecipes();
  }, []);

  function fetchRecipes() {
    setLoading(true);
    axios
      .post(`/retrieve-recipes/`, {
        ...reqData,
        page_size: 1000,
        page: 0,
        user_group: true,
      })
      .then(({ data }) => {
        setRecipes(data.response[0].data);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  if (loading)
    return (
      <Box
        style={{
          minHeight: "100px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Loader size="sm" color="teal" />
      </Box>
    );

  const options = recipes.map((m) => ({
    label: m.title,
    value: m.id,
  }));

  const selectedRecipe = recipes.find((m) => m.id === value);

  if (selectedRecipe) {
    return (
      <Box>
        <Box mb="sm">
          <RecipeCard asLink={false} recipe={selectedRecipe} />
        </Box>
        <ChooseAnotherButton onClick={() => onChange(null)} />
      </Box>
    );
  }

  return (
    <Grid gutter="sm">
      {recipes.length === 0 && (
        <Grid.Col
          span={12}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            minHeight: "100px",
          }}
        >
          <Text fw={600} size="lg">
            No recipes with snapshots found
          </Text>
        </Grid.Col>
      )}
      {recipes.map((recipe) => {
        const baseColor = recipe.table_categories.length
          ? tableCategoryConfig[`${recipe.table_categories[0]}`].color
          : "blue";
        return (
          <Grid.Col span={{ base: 12, md: 6 }} key={recipe.id}>
            <UnstyledButton
              style={{ width: "100%" }}
              onClick={() => onChange(recipe)}
            >
              <Box
                w="100%"
                mih={50}
                style={{
                  background: `var(--mantine-color-${baseColor}-1)`,
                  padding: "var(--mantine-spacing-xs)",
                  borderRadius: "var(--mantine-radius-md)",
                  border: `1px solid var(--mantine-color-${baseColor}-2)`,
                }}
              >
                <Flex gap="sm" align="center">
                  <div
                    style={{
                      color: `var(--mantine-color-${baseColor}-3)`,
                    }}
                  >
                    {entityIcon.report(30)}
                  </div>
                  <Stack gap={2} style={{ flexGrow: 1 }}>
                    <Text
                      fw={600}
                      size="md"
                      c={`var(--mantine-color-${baseColor}-8)`}
                    >
                      {recipe.title}
                    </Text>
                    {recipe.snapshot_count && (
                      <Text size="xs" c={`${baseColor}.9`} fw={500}>
                        {recipe.snapshot_count} snapshot
                        {recipe.snapshot_count > 1 ? "s" : ""}
                      </Text>
                    )}
                  </Stack>
                </Flex>
              </Box>
            </UnstyledButton>
          </Grid.Col>
        );
      })}
    </Grid>
  );

  return (
    <Select
      onChange={(e) => onChange(recipes.find((m) => m.id === e))}
      value={value}
      data={options}
      placeholder="Select a recipe"
      label="Report Recipe"
      searchable
    />
  );
}

function SnapshotSelect({ reqData, onChange, value, children }) {
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSnapshots();
  }, [JSON.stringify(reqData)]);

  function fetchSnapshots() {
    setLoading(true);
    axios
      .post(`/retrieve-report-snapshots/`, {
        ...reqData,
        page_size: 1000,
        page: 0,
      })
      .then(({ data }) => {
        setSnapshots(
          data.response.map((m) => ({
            ...m,
            show_in_bulk_communicator: false,
          }))
        );
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  if (loading)
    return (
      <Box
        style={{
          minHeight: "100px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Loader size="sm" color="teal" />
      </Box>
    );

  const options = snapshots.map((m) => ({
    label: m.title,
    value: m.id,
  }));

  const selectedSnapshot = snapshots.find((m) => m.id === value);

  if (snapshots.length === 0) {
    return (
      <Box
        mih={100}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
        }}
      >
        <Text fw={600} size="lg">
          No snapshots found
        </Text>
        <Box mt="md">{children}</Box>
      </Box>
    );
  }

  if (selectedSnapshot) {
    return (
      <Box>
        <SnapshotCard
          snapshot={selectedSnapshot}
          selectedId={selectedSnapshot.id}
          onSelect={(e) => true}
          showVisualizer={true}
        />
        <Flex mt="sm" justify="flex-end">
          <SnapshotData snapshot={selectedSnapshot} />
          <ChooseAnotherButton onClick={() => onChange(null)} />
        </Flex>
      </Box>
    );
  }

  return (
    <Grid>
      {snapshots.map((snapshot) => (
        <Grid.Col key={snapshot.id} span={{ base: 12, md: 6 }}>
          <SnapshotCard
            snapshot={snapshot}
            onSelect={() => onChange(snapshot)}
            showVisualizer={true}
          />
        </Grid.Col>
      ))}
    </Grid>
  );

  return (
    <Select
      onChange={(e) => onChange(snapshots.find((m) => m.id === e))}
      value={value}
      data={options}
      placeholder="Select a snapshot"
      label="Snapshot"
      searchable
    />
  );
}

function EffortSelect({ onChange, value, reqData }) {
  const [efforts, setEfforts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchValue, setSearchValue] = useState("");

  const CancelToken = axios.CancelToken;
  const cancelRef = useRef(null);

  useEffect(() => {
    fetchEfforts();
  }, [searchValue]);

  function fetchEfforts() {
    setLoading(true);

    const cancel = cancelRef.current;
    if (cancel) cancel();

    axios
      .post(
        `/segmentation/retrieve-efforts-for-user-group/`,
        {
          ...reqData,
          search_value: searchValue,
          page_size: 1000,
          page: 0,
        },
        {
          cancelToken: new CancelToken(function executor(c) {
            cancelRef.current = c;
          }),
        }
      )
      .then(({ data }) => {
        setEfforts(data.response);
      })
      .catch((err) => {
        setLoading(false);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  const selectedEffort = efforts.find((m) => m.id === value);

  if (selectedEffort) {
    return (
      <Box>
        <Box
          mb="sm"
          w="100%"
          mih={50}
          style={{
            background: `var(--mantine-color-${entityColor.effort}-1)`,
            padding: "var(--mantine-spacing-xs)",
            borderRadius: "var(--mantine-radius-md)",
            border: `1px solid var(--mantine-color-${entityColor.effort}-2)`,
          }}
        >
          <Flex gap="sm" align="center">
            <div
              style={{
                color: `var(--mantine-color-${entityColor.effort}-3)`,
              }}
            >
              {entityIcon.effort(30)}
            </div>
            <Stack gap={2} style={{ flexGrow: 1 }}>
              <Text
                fw={600}
                size="md"
                c={`var(--mantine-color-${entityColor.effort}-8)`}
              >
                {selectedEffort.internal_name}
              </Text>
              {selectedEffort.title !== selectedEffort.internal_name && (
                <Text size="xs" c={`${entityColor.effort}.8`} fw={500}>
                  {selectedEffort.title}
                </Text>
              )}
              {selectedEffort.start_date && selectedEffort.end_date && (
                <Text size="xs" c={`${entityColor.effort}.8`} fw={500}>
                  {new Date(selectedEffort.start_date).toLocaleDateString()} -{" "}
                  {new Date(selectedEffort.end_date).toLocaleDateString()}
                </Text>
              )}
            </Stack>
          </Flex>
        </Box>
        <ChooseAnotherButton onClick={() => onChange(null)} />
      </Box>
    );
  }

  return (
    <div>
      <Flex align="center" gap="sm">
        <TextInput
          placeholder="Search for efforts. Most recent displayed first."
          value={searchValue}
          size="lg"
          onChange={(e) => setSearchValue(e.target.value)}
          mb="sm"
          rightSection={
            loading && <Loader size="sm" color={entityColor.effort} />
          }
          style={{
            flexGrow: 1,
          }}
          styles={{
            wrapper: {
              "--input-bd-focus": entityColor.effort,
            },
          }}
        />
      </Flex>
      <Grid gutter="sm">
        {efforts.length === 0 && (
          <Grid.Col
            span={12}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              minHeight: "100px",
            }}
          >
            <Text fw={600} size="lg">
              No efforts found
            </Text>
          </Grid.Col>
        )}
        {efforts.map((effort) => {
          const baseColor = entityColor.effort;
          return (
            <Grid.Col span={{ base: 12, md: 6 }} key={effort.id}>
              <UnstyledButton
                style={{ width: "100%" }}
                onClick={() => onChange(effort)}
              >
                <Box
                  w="100%"
                  mih={50}
                  style={{
                    background: `var(--mantine-color-${baseColor}-1)`,
                    padding: "var(--mantine-spacing-xs)",
                    borderRadius: "var(--mantine-radius-md)",
                    border: `1px solid var(--mantine-color-${baseColor}-2)`,
                  }}
                >
                  <Flex gap="sm" align="center">
                    <div
                      style={{
                        color: `var(--mantine-color-${baseColor}-3)`,
                      }}
                    >
                      {entityIcon.effort(30)}
                    </div>
                    <Stack gap={2} style={{ flexGrow: 1 }}>
                      <Text
                        fw={600}
                        size="md"
                        c={`var(--mantine-color-${baseColor}-8)`}
                        lineClamp={1}
                        title={effort.internal_name}
                      >
                        {effort.internal_name}
                      </Text>
                      {effort.title !== effort.internal_name && (
                        <Text size="xs" c={`${baseColor}.8`} fw={500}>
                          {effort.title}
                        </Text>
                      )}
                      {effort.start_date && effort.end_date && (
                        <Text size="xs" c={`${baseColor}.8`} fw={500}>
                          {new Date(effort.start_date).toLocaleDateString()} -{" "}
                          {new Date(effort.end_date).toLocaleDateString()}
                        </Text>
                      )}
                    </Stack>
                  </Flex>
                </Box>
              </UnstyledButton>
            </Grid.Col>
          );
        })}
      </Grid>
    </div>
  );
}

const Section = ({
  title,
  description,
  children,
  transparent,
  completed,
  extra,
}) => {
  return (
    <div>
      <Flex
        align="flex-start"
        gap={{ base: "sm", md: extra ? "sm" : "xl" }}
        direction={{ base: "column", md: completed ? "row" : "column" }}
      >
        <Box maw={600} w={{ base: "100%", md: completed ? "45%" : "100%" }}>
          <Text size="lg" fw={600}>
            {title}
          </Text>
          <Text>{description}</Text>
        </Box>
        {extra && !completed && <Box>{extra}</Box>}
        <Box
          w={{ base: "100%", md: completed ? "55%" : "100%" }}
          style={{
            backgroundColor: transparent
              ? "transparent"
              : "var(--mantine-color-gray-0)",
            padding: transparent ? "0" : "var(--mantine-spacing-xs)",
            border: transparent
              ? "none"
              : "1px solid var(--mantine-color-gray-2)",
            borderRadius: "var(--mantine-radius-md)",
          }}
        >
          {children}
        </Box>
      </Flex>
    </div>
  );
};

const SnapshotData = ({ snapshot }) => {
  const [isOpen, setOpen] = useState(false);

  if (!snapshot.query_visualizer_data) return null;

  return (
    <>
      <Modal
        title={snapshot.title}
        size="xl"
        opened={isOpen}
        onClose={() => setOpen(false)}
      >
        <SnapshotQueryVisualizer data={snapshot.query_visualizer_data} />
      </Modal>
      <Button
        variant="subtle"
        color="gray.5"
        onClick={() => setOpen(true)}
        size="xs"
      >
        View Snapshot Data
      </Button>
    </>
  );
};

const ChooseAnotherButton = ({ onClick }) => {
  return (
    <Flex justify="flex-end">
      <Button variant="subtle" color="gray.5" onClick={onClick} size="xs">
        Choose another
      </Button>
    </Flex>
  );
};

const SourceSelect = ({ onChange, value }) => {
  return (
    <Flex mb="md" gap="sm" direction={{ base: "column", md: "row" }}>
      {sourceModes.map((mode) => (
        <Button
          color={mode.color}
          variant={value === mode.value ? "filled" : "light"}
          key={mode.value}
          onClick={() => {
            onChange(mode.value);
          }}
          leftSection={mode.icon}
        >
          {mode.label}
        </Button>
      ))}
    </Flex>
  );
};
